"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { ulid } from "ulid"

import { createDiscountDisplayOptions, determineCardPrices } from "@/lib/price-utils"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { showProductPrice } from "@/lib/storefront/product/productHelper"
import {
  buildCategoryListingAttribution,
  clearListingAttribution,
  storeListingAttribution,
} from "@/lib/tracking/listing-attribution"
import { storeFactFinderSessionAttribution } from "@/lib/tracking/factfinder-session-attribution"
import FFProductClickTrackingClient from "@/components/product/FFProductClickTrackingClient"
import FFRecommendationClickTrackingClient from "@/components/product/FFRecommendationClickTrackingClient"
import ProductCardDiscount from "@/components/product/ProductCardDiscount"
import ProductCardPrice from "@/components/product/ProductCardPrice"

import { getResizedSbImagepath } from "../ui/helpers/resize-sb-image"

export default function ProductCardLandscape(props: {
  productData: ReducedProductData | undefined
  /*
  standardVariant: ReducedProductData | undefined
*/
  showDescription: boolean | undefined
  prices: any
  priceLoading?: boolean
  isB2BCustomer: boolean
  generalDiscount?: number
  customerCategoryDiscount?: number
  isoLocale: string
  inStoryBlok: boolean
  ffSearchParamsEncrypted?: string
  categoryPath?: string[]
  position?: number
  trackingList?: string
  searchTerm?: string
  campaignId?: string
  isRecommendation?: boolean
  mainProductId?: string
}) {
  // originalProductId (Master-ID) mitgeben, damit isSinglePrice nicht fälschlich
  // als Einzelpreis erkannt wird, wenn Preise von cheapestVariant stammen. So wird
  // das "Ab"-Label korrekt angezeigt.
  const cardPrices = determineCardPrices(
    props.isoLocale,
    props.isB2BCustomer,
    props.prices,
    props.productData?.ecoTax,
    undefined,
    props.productData?.id,
    createDiscountDisplayOptions(props.productData, props.generalDiscount, props.customerCategoryDiscount)
  )
  const { showPrice } = showProductPrice(props.productData, cardPrices)
  const [trackingMarker, setTrackingMarker] = useState<string | null>(null)

  useEffect(() => {
    setTrackingMarker(ulid())
  }, [])

  const handleProductClick = () => {
    if (props.categoryPath && props.productData?.id) {
      storeListingAttribution(
        buildCategoryListingAttribution(
          props.categoryPath,
          props.productData.id,
          props.position ? props.position - 1 : undefined
        )
      )
    } else {
      clearListingAttribution()
    }
    storeFactFinderSessionAttribution({
      productId: props.productData?.id,
      trackingList: props.trackingList,
      searchTerm: props.searchTerm,
      position: props.position,
    })
  }

  return (
    <>
      {props.productData ? (
        <Link
          prefetch={false}
          data-tracking-marker={trackingMarker}
          className="@container/productcard size-full"
          href={props.productData?.link ?? ""}
          onClick={handleProductClick}
        >
          {trackingMarker && (
            <>
              {props.isRecommendation && props.mainProductId ? (
                <FFRecommendationClickTrackingClient
                  trackingMarker={trackingMarker}
                  productData={props.productData}
                  mainProductId={props.mainProductId}
                />
              ) : (
                <FFProductClickTrackingClient
                  trackingMarker={trackingMarker}
                  productData={props.productData}
                  trackingList={props.trackingList ?? "direct"}
                  position={props.position ?? 1}
                  searchTerm={props.searchTerm}
                  campaignId={props.campaignId}
                />
              )}
            </>
          )}
          <ProductCardDiscount
            isoLocale={props.isoLocale}
            product={props.productData}
            variant={"landscape"}
            prices={props.prices}
            isB2BCustomer={props.isB2BCustomer}
            _cardPrices={cardPrices}
            legacyPrices={true}
          />
          {/*inner main div*/}
          <div className="flex h-full flex-1 grow flex-row justify-between gap-2 p-2 2xl:p-4">
            {/*top half*/}
            <div className="flex w-1/3 flex-col items-center justify-center gap-2">
              {/*image*/}
              <div className={"flex items-center"}>
                {props.productData.image ? (
                  <Image
                    unoptimized={true}
                    className="aspect-square max-h-20 overflow-hidden object-contain"
                    alt={props.productData.title}
                    src={getResizedSbImagepath(props.productData.image)}
                    width={props.productData.image_width}
                    height={props.productData.image_height}
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
            <div className={"flex w-2/3 flex-col items-end justify-between gap-1"}>
              {/*title*/}
              <div className={"flex w-full justify-start"}>
                <p className="line-clamp-2 min-h-8 text-sm/tight font-bold text-gray-900">{props.productData.title}</p>
              </div>
              {/*description*/}
              {props.showDescription && (
                <div className={"prose-xs line-clamp-2 hidden w-full grow justify-start text-xs/tight 2xl:flex"}>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: props.productData.description ? props.productData.description : "",
                    }}
                  />
                </div>
              )}

              {/*right half*/}
              <div className="flex flex-col justify-end gap-2">
                {/*prices and buttons*/}
                <ProductCardPrice
                  isoLocale={props.isoLocale}
                  showEcoTaxFlag={false}
                  productData={props.productData}
                  cardPrices={cardPrices}
                  isB2BCustomer={props.isB2BCustomer}
                  showPrice={showPrice}
                  priceLoading={props.priceLoading}
                />
              </div>
            </div>
          </div>
        </Link>
      ) : (
        <>{props.inStoryBlok && <p>Product not found</p>}</>
      )}
    </>
  )
}
